import React, { FC } from 'react';
import './UIButton.css';
import { 
	FaEdit, 
	FaFilePdf, 
	FaLock, 
	FaPlus, 
	FaPlusCircle, 
	FaRedo, 
	FaSpinner, 
	FaTrash, 
	FaTrashAlt, 
} from '@/Shared/Lib';
import { UIAction, UIActionIcon } from '@/Shared/Types';
import { ActionButton } from '@/Shared/UI';

/**
 * Mapping between UIActionIcon keys and actual JSX icons.
 * Used to render the correct icon for each UI action.
 */
export const UI_ACTION_ICONS: Record<UIActionIcon, JSX.Element> = {
  FaTrash: <FaTrash />,
  FaRedo: <FaRedo />,
  FaEdit: <FaEdit />,
  FaPlusCircle: <FaPlusCircle />,
  FaFilePdf: <FaFilePdf />,
  FaLock: <FaLock />,
  FaTrashAlt: <FaTrashAlt />
};

/**
 * Props for UIButton component
 */
interface UIButtonProps {
  /** The UIAction object defining label, icon, onClick, tooltip, etc. */
  action: UIAction;

  /** Whether the current viewport is desktop size (controls label visibility) */
  isDesktop: boolean;
}

/**
 * =====================================================
 * UIButton
 * =====================================================
 *
 * Renders a single UI action button based on a UIAction object.
 * Handles icon rendering, loading state, disabled state, tooltip,
 * and responsive label visibility.
 *
 * @param action - The UIAction to render
 * @param isDesktop - Flag indicating desktop viewport
 * @returns A React element representing the action button
 *
 * Author: Samano CASTRE
 * Date: February 27, 2026
 */
const UIButton: FC<UIButtonProps> = ({action, isDesktop}) => {
	return (
		<ActionButton
			label={action.label}
			onClick={action.onClick}
			disabled={action.disabled}
			isLoading={action.loading}
			tooltip={action.tooltip}
			classNames={action.type}
			showLabel={action.responsive ? isDesktop : true}
			icon={UI_ACTION_ICONS[action.icon]}
		/>
	);
}

export default UIButton;
